/**
 * lib/collapse-section — СТВОЛ: переиспользуемая механика ПЛАВНОГО раскрытия/свёртки
 * блока по ИЗМЕРЕННОЙ высоте контента (accordion-height animator).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (owner-bug 2026-06-24 «таблица в профиле прыгает при тогле
 * ачивок», тикет frontend-lead `profile-achievements-anchor.md`):
 *   Прежняя реализация в Профиле раскрывала грид значков через `max-height: none` при
 *   `transition: max-height` — CSS НЕ анимирует переход в/из `none`, поэтому раскрытие
 *   СНАПало мгновенно (рывок), а свёртка fixed→fixed анимировалась → асимметрия «открылось
 *   рывком / свернулось плавно» + реверстка соседей толкала таблицу. Корень — нельзя
 *   анимировать высоту в `none`. Этот ствол даёт ОДИН безкаповый АНИМИРУЕМЫЙ приём:
 *   collapsed = CSS-формула 1 ряда (нижний размер задаёт потребитель в CSS), expanded =
 *   ИЗМЕРЕННАЯ px-высота контента (`scrollHeight`) — px-значение АНИМИРУЕТСЯ симметрично в
 *   обе стороны, БЕЗ кап-константы (предел = реальный контент, не магическое число §0.G.5).
 *
 *   Механика общая (аккордеон по высоте нужен везде, где блок сворачивается «в N строк ↔
 *   весь контент») → ствол в lib/, а НЕ копия в экран (owner-directive 2026-05-30 «ствол →
 *   ветки»). Потребитель монтирует тонкой веткой: даёт целевой элемент (грид) + хост-класс
 *   состояния (`--open`) + длительность-токен. Дублирование вместо ствола = REJECT ревьюера.
 *
 * SCOPE: ПРИВЯЗАТЬ поведение к УЖЕ ОТРЕНДЕРЕННОМУ элементу (attach-not-rerender §3) —
 *   измерять контент, гнать `max-height` (px↔px) с симметричным `transition`, держать
 *   collapsed-состояние через CSS-формулу потребителя (модуль НЕ хардкодит размер 1 ряда),
 *   а expanded — через измеренный px. Reduce-motion → мгновенно (transition снят, позиция
 *   идентична). Re-measure на каждое раскрытие (учёт ресайза/смены контента).
 *   NOT IN SCOPE: размер collapsed-состояния (CSS потребителя — формула от --cell-size),
 *   каретка/aria (lib/disclosure-caret или сам экран), данные/рендер контента.
 *
 * a11y: модуль НЕ владеет aria — потребитель синхронизирует `aria-expanded`/каретку
 *   (Профиль — `syncAchToggle`). Тактильность кнопки-триггера — на стороне потребителя.
 *
 * Cross-references: тикет profile-achievements-anchor.md (AC-2/AC-4/AC-11);
 *   smooth-motion-animation §8.4 (симметрия open/close); responsive-ui §0.G.5 (без капа) /
 *   §0.E (reduce-motion); styles/profile.css (.badge-strip-grid — ветка-потребитель).
 */

export interface CollapseSectionOptions {
  /**
   * Целевой элемент, чья высота анимируется (напр. `.badge-strip-grid`). Должен иметь в
   * CSS `overflow: hidden` и collapsed-`max-height` (формула 1 ряда — НЕ задаём здесь, это
   * размер ветки). Модуль навешивает inline `max-height` (px) только на время/после анимации.
   */
  readonly target: HTMLElement;
  /**
   * CSS-длительность анимации (строка для `transition`, напр. `'160ms'` или
   * `var(...)`-производное через getComputedStyle потребителя). Один источник темпа —
   * не сырой magic в нескольких местах (§8.4). По умолчанию читается из `--pf-reveal-dur`
   * на target/документе, иначе fallback-токен ниже.
   */
  readonly durationMs?: number;
  /**
   * Стартовое состояние (раскрыто?). По умолчанию false (свёрнуто).
   */
  readonly initialOpen?: boolean;
}

export interface CollapseSectionHandle {
  /** Раскрыть (анимированно вниз). Идемпотентно. */
  open(): void;
  /** Свернуть (анимированно вверх). Идемпотентно. */
  close(): void;
  /** Переключить. Возвращает новое состояние. */
  toggle(): boolean;
  /** Текущее состояние (раскрыто?). */
  isOpen(): boolean;
  /** Снять inline-стили/слушатели (для размонтирования экраном). */
  detach(): void;
}

/** Fallback-длительность, если потребитель не задал и --pf-reveal-dur не определён (мс). */
const DEFAULT_DURATION_MS = 160;

/** Прочитать длительность из CSS-переменной --pf-reveal-dur (если есть), иначе fallback. */
function resolveDurationMs(target: HTMLElement, override?: number): number {
  if (typeof override === 'number') return override;
  const raw = getComputedStyle(target).getPropertyValue('--pf-reveal-dur').trim();
  if (raw.endsWith('ms')) {
    const n = parseFloat(raw);
    if (Number.isFinite(n) && n > 0) return n;
  }
  if (raw.endsWith('s')) {
    const n = parseFloat(raw) * 1000;
    if (Number.isFinite(n) && n > 0) return n;
  }
  return DEFAULT_DURATION_MS;
}

/** Эмулятор/реальный matchMedia: пользователь просит мгновенно? */
function prefersReducedMotion(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Смонтировать аккордеон-по-высоте на ГОТОВЫЙ элемент. Возвращает handle с open/close/toggle.
 *
 * ПРИНЦИП (почему так — корень owner-bug):
 *   - collapsed: снимаем inline max-height → действует CSS-формула 1 ряда потребителя
 *     (collapsed-высота = размер ветки, без капа-константы в модуле).
 *   - expanded: измеряем `scrollHeight` (реальная высота контента) и ставим inline
 *     `max-height: <px>` — px-значение АНИМИРУЕТСЯ (в отличие от `none`), симметрично.
 *   - анимация: переключаем inline max-height между collapsed-px (измеренный clientHeight в
 *     свёрнутом) и expanded-px, с `transition: max-height <dur>`; reduce-motion → без transition.
 */
export function mountCollapseSection(opts: CollapseSectionOptions): CollapseSectionHandle {
  const { target } = opts;
  let open = opts.initialOpen ?? false;
  let durationMs = resolveDurationMs(target, opts.durationMs);
  // Идентификатор последнего запрошенного перехода — гасит гонку быстрых кликов
  // (открыл→закрыл до конца анимации): устаревший transitionend не «защёлкнет» max-height.
  let txnSeq = 0;

  /** Высота свёрнутого состояния (px) — РОВНО CSS-формула 1 ряда. Меряем без inline max-height. */
  function measureCollapsedPx(): number {
    const prev = target.style.maxHeight;
    target.style.maxHeight = ''; // вернуть управление CSS-правилу (collapsed-формула).
    // clientHeight в свёрнутом классе = высота 1 ряда (CSS max-height ограничивает overflow:hidden).
    const h = target.clientHeight;
    target.style.maxHeight = prev;
    return h;
  }

  /** Полная высота контента (px) — все ряды. */
  function measureContentPx(): number {
    return target.scrollHeight;
  }

  function clearTransition(): void {
    target.style.transition = '';
  }

  function applyInstant(toOpen: boolean): void {
    // Reduce-motion / без анимации: позиция идентична штатной (AC-5), просто без tween.
    clearTransition();
    if (toOpen) {
      // Раскрыто без анимации: снять max-height вовсе — действует контент (overflow:visible
      // отдаёт CSS-правило --open). Ставим достаточный px, затем чистим на след. кадре,
      // чтобы итоговая высота = контент (стабильна для rect-инвариантов).
      target.style.maxHeight = `${measureContentPx()}px`;
    } else {
      target.style.maxHeight = ''; // вернуть collapsed-формуле.
    }
  }

  function animateTo(toOpen: boolean): void {
    const seq = ++txnSeq;
    durationMs = resolveDurationMs(target, opts.durationMs);

    if (prefersReducedMotion()) {
      applyInstant(toOpen);
      return;
    }

    const collapsedPx = measureCollapsedPx();
    const contentPx = measureContentPx();
    const fromPx = toOpen ? collapsedPx : contentPx;
    const toPx = toOpen ? contentPx : collapsedPx;

    // 1) Зафиксировать стартовую высоту в px БЕЗ transition (избежать рывка с auto/none).
    clearTransition();
    target.style.maxHeight = `${fromPx}px`;
    // Принудительный reflow — чтобы браузер «увидел» стартовую точку до включения transition.
    void target.offsetHeight;

    // 2) Включить transition и поехать в целевую px-высоту (АНИМИРУЕТСЯ — это и есть фикс снапа).
    target.style.transition = `max-height ${durationMs}ms ease`;
    target.style.maxHeight = `${toPx}px`;

    const onEnd = (e: TransitionEvent): void => {
      if (e.propertyName !== 'max-height') return;
      finish();
    };

    function finish(): void {
      if (seq !== txnSeq) return; // более новый переход уже идёт — этот устарел.
      target.removeEventListener('transitionend', onEnd);
      clearTransition();
      if (toOpen) {
        // Раскрыто: держим max-height = реальный контент (равен контенту, НЕ кап-константа
        // §0.G.5 — предел = сам контент). Это даёт стабильный rect для AC-1.
        target.style.maxHeight = `${measureContentPx()}px`;
      } else {
        // Свёрнуто: вернуть управление CSS-формуле 1 ряда.
        target.style.maxHeight = '';
      }
    }

    target.addEventListener('transitionend', onEnd);
    // Подстраховка от потерянного transitionend (фоновая вкладка и т.п.).
    window.setTimeout(finish, durationMs + 80);
  }

  function setOpen(next: boolean): void {
    if (next === open) return;
    open = next;
    animateTo(open);
  }

  // Инициализация стартового состояния без анимации.
  applyInstant(open);

  return {
    open: () => setOpen(true),
    close: () => setOpen(false),
    toggle(): boolean {
      setOpen(!open);
      return open;
    },
    isOpen: () => open,
    detach(): void {
      txnSeq++;
      clearTransition();
      target.style.maxHeight = '';
    },
  };
}
